import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth, Surface } from "../context/AuthContext";
import { useBranding } from "../lib/branding";
import { useResource } from "../lib/hooks";
import { Feature, FEATURE_LABELS, useEntitlements } from "../lib/entitlements";
import { NotificationBell } from "./NotificationBell";
import type { AppRole } from "../lib/types";

/**
 * Stroke icon set, Lucide-style, one path bundle per name. Inline rather than
 * a dependency: fourteen icons do not justify a package, and inline SVG
 * inherits currentColor so the nav states colour them for free.
 */
const ICON_PATHS: Record<string, JSX.Element> = {
    dashboard: <><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></>,
    caseload: <><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M12 12v3" /><path d="M10.5 13.5h3" /></>,
    pipeline: <><path d="M4 4h6v9H4z" /><path d="M14 4h6v5h-6z" /><path d="M14 13h6v7h-6z" /><path d="M4 17h6v3H4z" /></>,
    referrals: <><path d="M22 12h-6l-2 3h-4l-2-3H2" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /></>,
    incidents: <><circle cx="12" cy="12" r="9" /><path d="M12 8v4" /><path d="M12 16h.01" /></>,
    quotes: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M9 13h6" /><path d="M9 17h4" /></>,
    inquiries: <><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></>,
    contacts: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
    companies: <><rect x="4" y="2" width="16" height="20" rx="1" /><path d="M9 22v-4h6v4" /><path d="M8 6h.01M16 6h.01M8 10h.01M16 10h.01M8 14h.01M16 14h.01" /></>,
    analytics: <><path d="M3 3v18h18" /><rect x="7" y="12" width="3" height="6" /><rect x="12" y="8" width="3" height="10" /><rect x="17" y="4" width="3" height="14" /></>,
    compliance: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="m9 12 2 2 4-4" /></>,
    policies: <><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></>,
    staff: <><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="9" cy="10" r="2" /><path d="M15 8h4M15 12h4M5 16c.8-1.3 2.3-2 4-2s3.2.7 4 2" /></>,
    settings: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></>,
    rostering: <><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></>,
    tasks: <><rect x="3" y="3" width="18" height="18" rx="2" /><path d="m9 12 2 2 4-4" /></>,
    training: <><circle cx="12" cy="8" r="6" /><path d="M15.5 13 17 22l-5-3-5 3 1.5-9" /></>,
    leave: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 3" /></>,
    participants: <><path d="M19 14c1.5-1.4 3-3.1 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.8 0-3 .5-4.5 2-1.5-1.5-2.7-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.4 1.5 4.1 3 5.5l7 7z" /></>,
    help: <><circle cx="12" cy="12" r="9" /><path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3" /><path d="M12 17h.01" /></>,
    billing: <><path d="M12 2v20" /><path d="M17 5.5H9.5a3 3 0 0 0 0 6h5a3 3 0 0 1 0 6H6.5" /></>,
    timesheet: <><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /><path d="M12 14v3" /><path d="M10.5 15.5h3" /></>,
    updates: <><path d="m3 11 18-5v12L3 14v-3z" /><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" /></>,
    clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 3" /></>,
};

const Icon = ({ name }: { name: string }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
         aria-hidden="true" className="nav-icon">
        {ICON_PATHS[name] ?? ICON_PATHS.dashboard}
    </svg>
);

interface NavItem {
    to: string;
    label: string;
    icon: string;
    /** Roles permitted to see this item. Omitted means everyone signed in. */
    roles?: AppRole[];
    /** Permission key: when the user has ANY explicit grants, this must be among them. */
    permission?: string;
    /**
     * Q1: visible when ANY of these resolved quality permissions is true.
     * Unlike `roles`, this follows what the person may DO - a delegated
     * investigator sees the door their grant opens, whatever their role.
     */
    quality?: string[];
    /** PS0: plan flag. When off, the item renders shown-and-locked (upsell). */
    feature?: Feature;
}

interface NavGroup {
    heading?: string;
    items: NavItem[];
}

/**
 * Two surfaces, one shell.
 *
 * ShiftFlow is the workforce portal; the CRM carries the management system's
 * full module set, flat like the reference design. The toggle only appears for
 * accounts allowed into both; staff have no CRM grant, and a /crm URL typed by
 * hand lands on the access-request screen while the API refuses the data
 * independently.
 *
 * Permission filtering here is a courtesy, same as role filtering — row-level
 * security in the database decides what any page can actually load.
 */
const SHIFTFLOW_GROUPS: NavGroup[] = [
    {
        heading: "Operate",
        items: [
            { to: "/", label: "Dashboard", icon: "dashboard", permission: "shiftflow.dashboard" },
            { to: "/reports", label: "Reports", icon: "analytics", roles: ["admin", "manager", "supervisor"] },
            { to: "/rostering", label: "Rostering", icon: "rostering", permission: "shiftflow.rostering" },
            { to: "/time-clock", label: "Time Clock", icon: "clock", roles: ["admin", "manager", "supervisor", "staff"], permission: "shiftflow.time_clock" },
            { to: "/forms", label: "Forms & checklists", icon: "tasks", roles: ["admin", "manager", "supervisor", "staff"] },
            { to: "/site-tasks", label: "Site tasks", icon: "tasks", roles: ["admin", "manager", "supervisor", "staff"], permission: "shiftflow.site_tasks" },
        ],
    },
    {
        heading: "People",
        items: [
            { to: "/employees", label: "Employees", icon: "contacts", roles: ["admin", "manager", "supervisor", "staff"], permission: "shiftflow.employees" },
            { to: "/smart-groups", label: "Smart groups", icon: "analytics", roles: ["admin", "manager"] },
            { to: "/org-chart", label: "Org chart", icon: "staff", roles: ["admin", "manager", "supervisor"] },
            { to: "/onboarding-admin", label: "Onboarding", icon: "contacts", roles: ["admin", "manager", "supervisor"] },
            { to: "/training", label: "Training & compliance", icon: "training", roles: ["admin", "manager", "supervisor", "staff"], permission: "shiftflow.training" },
            { to: "/compliance", label: "Document expiry", icon: "training", roles: ["admin", "manager"] },
            { to: "/leave", label: "Availability & leave", icon: "leave", roles: ["admin", "manager", "supervisor", "staff"], permission: "shiftflow.leave" },
        ],
    },
    {
        heading: "Service",
        items: [
            { to: "/participants", label: "Participants", icon: "participants", roles: ["admin", "manager", "supervisor", "staff"], permission: "shiftflow.participants" },
            { to: "/progress-notes", label: "Progress notes", icon: "quotes", roles: ["admin", "manager", "supervisor", "staff"] },
            { to: "/provider-portal", label: "Provider portal", icon: "companies", roles: ["admin", "manager"] },
        ],
    },
    {
        heading: "Governance",
        items: [
            { to: "/incidents", label: "Incidents", icon: "incidents", permission: "shiftflow.incidents" },
            { to: "/policies", label: "Policies", icon: "policies", permission: "shiftflow.policies" },
            { to: "/updates", label: "Updates", icon: "updates", permission: "shiftflow.updates" },
            { to: "/chat", label: "Chat", icon: "inquiries" },
            { to: "/help", label: "Help & FAQ", icon: "help" },
            { to: "/settings", label: "Settings", icon: "settings", roles: ["admin"] },
        ],
    },
];

/**
 * What a partner organisation sees: their clients, and the invoices we have
 * issued to them. Nothing else exists for them — no toggle, no other modules.
 */
const PARTNER_GROUPS: NavGroup[] = [
    {
        items: [
            { to: "/partner", label: "My clients", icon: "participants" },
            { to: "/partner/requests", label: "Requests", icon: "inquiries" },
            { to: "/partner/invoices", label: "Invoices", icon: "billing" },
            { to: "/partner/policies", label: "Policies", icon: "policies" },
            { to: "/partner/organisation", label: "My organisation", icon: "companies" },
            { to: "/partner/messages", label: "Messages", icon: "inquiries" },
            { to: "/help", label: "Help & FAQ", icon: "help" },
        ],
    },
];

/**
 * What an IT support account sees: nothing but the account helpdesk tools it
 * was created for. No ShiftFlow, no CRM, no participant or financial data —
 * the API refuses those independently, but there is no nav link inviting
 * anyone to find out.
 */
const SUPPORT_GROUPS: NavGroup[] = [
    {
        items: [
            { to: "/support", label: "Accounts", icon: "staff" },
            { to: "/help", label: "Help & FAQ", icon: "help" },
        ],
    },
];

/** The CRM nav: the management system's full tab set, flat, icon-led. */
const CRM_GROUPS: NavGroup[] = [
    {
        items: [
            { to: "/crm", label: "Dashboard", icon: "dashboard", permission: "crm.dashboard" },
            { to: "/crm/caseload", label: "Caseload", icon: "caseload" },
            { to: "/crm/pipeline", label: "Pipeline", icon: "pipeline", permission: "crm.pipeline" },
            { to: "/referrals", label: "Referrals", icon: "referrals", roles: ["admin", "manager", "viewer"], permission: "crm.referrals" },
            // One entry for both enquiry streams — the website queue and the
            // business register live behind the same door, Australian spelling.
            { to: "/enquiry", label: "Enquiries", icon: "inquiries", permission: "crm.inquiries" },
            { to: "/applicants", label: "Applicants", icon: "staff", roles: ["admin", "manager"] },
            { to: "/website-inbox", label: "Website inbox", icon: "inquiries", roles: ["admin", "manager"] },
            { to: "/cases", label: "Cases", icon: "caseload", roles: ["admin", "manager", "supervisor"] },
            { to: "/incidents", label: "Incidents", icon: "incidents" },
            // Q3: submitters get the door too. The page itself shows a
            // submit-only user the file buttons and no register, and the API
            // holds every request to the caller's resolved permissions again.
            { to: "/quality", label: "Quality registers", icon: "compliance",
              quality: ["quality.records.view", "quality.incident.investigate", "quality.complaint.investigate",
                        "quality.incident.submit", "quality.complaint.submit",
                        "quality.feedback.submit", "quality.compliment.submit"] },
            { to: "/crm/quotes", label: "Quotes", icon: "quotes", permission: "crm.quotes" },
            { to: "/crm/ndis-pricing", label: "NDIS Pricing", icon: "billing", roles: ["admin", "manager"] },
            { to: "/crm/contacts", label: "Contacts", icon: "contacts", permission: "crm.contacts" },
            { to: "/crm/companies", label: "Companies", icon: "companies", permission: "crm.companies" },
            { to: "/crm/analytics", label: "Analytics", icon: "analytics", permission: "crm.analytics" },
            // C2: the first non-quality door opened by the matrix itself.
            { to: "/crm/reports", label: "Reports", icon: "analytics",
              quality: ["reports.run", "reports.manage"] },
            { to: "/crm/invoices", label: "Invoices", icon: "billing", roles: ["admin", "manager"], permission: "crm.invoices" },
            { to: "/crm/allocations", label: "Allocations", icon: "billing", roles: ["admin", "manager"], permission: "crm.invoices" },
            { to: "/crm/partner-requests", label: "Partner requests", icon: "inquiries", roles: ["admin", "manager"], permission: "crm.partner_requests" },
            { to: "/crm/finance", label: "Finance", icon: "analytics", roles: ["admin", "manager"], permission: "crm.finance" },
            { to: "/crm/timesheets", label: "Timesheets", icon: "timesheet", roles: ["admin", "manager"], permission: "crm.timesheets" },
            { to: "/crm/compliance", label: "Compliance", icon: "compliance", permission: "crm.compliance" },
            { to: "/crm/compliance-dashboard", label: "Compliance dashboard", icon: "compliance", permission: "crm.compliance" },
            { to: "/policies", label: "Policies", icon: "policies" },
            { to: "/crm/staff", label: "Staff", icon: "staff", roles: ["admin", "manager"], permission: "crm.staff" },
            { to: "/settings", label: "Settings", icon: "settings", roles: ["admin"] },
        ],
    },
];

const TITLES: Record<string, { title: string; subtitle: string }> = {
    "/": { title: "Dashboard", subtitle: "Workforce, compliance and service delivery at a glance" },
    "/rostering": { title: "Rostering", subtitle: "Every shift is checked against the compliance rules before it saves" },
    "/time-clock": { title: "Time Clock", subtitle: "Clock in and out, and review attendance" },
    "/site-tasks": { title: "Site tasks", subtitle: "Operational tasks by location" },
    "/forms": { title: "Forms & checklists", subtitle: "Templates staff fill on shift — submissions come back for review" },
    "/updates": { title: "Updates", subtitle: "Company and NDIS news, in one place" },
    "/chat": { title: "Chat", subtitle: "Team channels — messages are part of the workplace record" },
    "/employees": { title: "Employees", subtitle: "Workforce records and compliance status" },
    "/smart-groups": { title: "Smart groups", subtitle: "Live rule-based segments — derived, not stored, always current" },
    "/org-chart": { title: "Org chart", subtitle: "Built live from each employee's direct manager" },
    "/reports": { title: "Reports", subtitle: "This week at a glance — computed live from the roster" },
    "/onboarding-admin": { title: "Onboarding", subtitle: "Track and approve staff onboarding" },
    "/compliance": { title: "Document expiry", subtitle: "Certificates and checks nearing or past expiry" },
    "/training": { title: "Training & compliance", subtitle: "Competency evidence and expiry" },
    "/leave": { title: "Availability & leave", subtitle: "When people can and cannot be rostered" },
    "/participants": { title: "Participants", subtitle: "People supported, their workers and their roster" },
    "/progress-notes": { title: "Progress notes", subtitle: "Shift-linked care documentation — append-only, audit-ready" },
    "/provider-portal": { title: "Provider Portal", subtitle: "Preview — what a partner provider sees in their own portal login" },
    "/referrals": { title: "Referrals", subtitle: "Intake and episodes of service" },
    "/enquiry": { title: "Enquiries", subtitle: "Website contact and callback requests, and business enquiries from organisations — two streams, one door" },
    "/applicants": { title: "Applicants", subtitle: "Talent pool, job applications and expressions of interest — hiring someone is a separate step" },
    "/quality": { title: "Quality registers", subtitle: "Incident reports, investigations and feedback — each organisation sees only its own" },
    "/crm/reports": { title: "Reports", subtitle: "Pick fields, get a table, export — every report scoped to whoever runs it" },
    "/website-inbox": { title: "Website inbox", subtitle: "Other website forms — newsletter, feedback, complaints" },
    "/cases": { title: "Cases", subtitle: "One file per episode of care — participant, provider, referral, and everything delivered" },
    "/providers": { title: "Providers & contacts", subtitle: "Organisations you work with, and the people at them" },
    "/incidents": { title: "Incidents", subtitle: "Incident register and follow-up" },
    "/policies": { title: "Policies", subtitle: "Current policy library and acknowledgements" },
    "/settings": { title: "Settings & access", subtitle: "Accounts, surfaces and module permissions" },
    "/help": { title: "Help & FAQ", subtitle: "Find answers in the FAQ, or ask the team a question" },
    "/support": { title: "Accounts", subtitle: "Reset a password or reactivate a locked-out account" },
    "/partner": { title: "My clients", subtitle: "Your clients' rosters, allocated workers and progress notes" },
    "/partner/invoices": { title: "Invoices", subtitle: "Invoices issued to your organisation — view, download and pay" },
    "/partner/requests": { title: "Requests", subtitle: "Refer, request cover, quotes, services — and see our responses" },
    "/partner/policies": { title: "Policies", subtitle: "Policies and procedures that apply to your organisation" },
    "/partner/messages": { title: "Messages", subtitle: "Talk to the TrustCare team — replies land here with an unread badge" },
    "/crm/partner-requests": { title: "Partner requests", subtitle: "Referrals, cover, quotes, complaints and incidents from partner organisations" },
    "/crm/finance": { title: "Finance", subtitle: "Income, expenditure, rates and year-end statements" },
    "/my-pay": { title: "My pay", subtitle: "Your timesheets and payslips — printable payment evidence" },
    "/my-profile": { title: "My profile", subtitle: "Your details and sign-in password" },
    "/partner/organisation": { title: "My organisation", subtitle: "Your organisation's details and contacts — what you save here is what TrustCare sees" },
    "/crm/invoices": { title: "Invoices", subtitle: "Bill customer organisations for delivered shifts" },
    "/crm/allocations": { title: "Allocations", subtitle: "Delivered shifts, the service billed and the rate applied" },
    "/crm/timesheets": { title: "Timesheets", subtitle: "Pay staff for delivered shifts" },
    "/crm": { title: "CRM dashboard", subtitle: "Pipeline, inquiries and quoting at a glance" },
    "/crm/caseload": { title: "Caseload", subtitle: "Participants and their episodes of service" },
    "/crm/analytics": { title: "Analytics", subtitle: "Pipeline value and conversion" },
    "/crm/pipeline": { title: "Pipeline", subtitle: "Leads by stage, from inquiry to closed" },
    "/crm/quotes": { title: "Quotes", subtitle: "Quotes, line items and status" },
    "/crm/ndis-pricing": { title: "NDIS Pricing", subtitle: "Official NDIS price caps — the ceiling your charge sits under" },
    "/crm/inquiries": { title: "Enquiries", subtitle: "Business enquiries — organisations asking about services" },
    "/crm/companies": { title: "Companies", subtitle: "Organisations in the pipeline" },
    "/crm/contacts": { title: "Contacts", subtitle: "People at the organisations you work with" },
    "/crm/compliance": { title: "Compliance", subtitle: "Policy currency, acknowledgements and reportable incidents" },
    "/crm/compliance-dashboard": { title: "Compliance dashboard", subtitle: "Seven panels, each scoped to what your role may read" },
    "/crm/staff": { title: "Staff", subtitle: "Workforce directory" },
};

const initials = (name: string): string =>
    name.split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((p) => p[0]!.toUpperCase()).join("");

export const Layout = () => {
    const { user, signOut, surface, setSurface, mayAccess, mayQuality } = useAuth();
    const branding = useBranding();
    const location = useLocation();

    // Unread-message total for the nav badge on Chat / Messages. Refreshed on
    // every route change — cheap query, and reading the conversation is
    // itself a navigation, so the badge clears the moment it should.
    const unread = useResource<Array<{ channel_id: string; unread: number }>>("/chat-unread");
    const unreadTotal = (unread.data ?? []).reduce((sum, row) => sum + row.unread, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { unread.reload(); }, [location.pathname]);
    const badgeFor = (to: string) =>
        (to === "/chat" || to === "/partner/messages") && unreadTotal > 0 ? unreadTotal : null;

    // PS0: plan flags for shown-and-locked premium nav items.
    const entitlements = useEntitlements();

    // The account menu under the person's name: their personal pages live
    // here, not in the module nav — a manager is a worker too, and their own
    // pay and profile belong to the person, not to a surface.
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => setMenuOpen(false), [location.pathname]);
    useEffect(() => {
        if (!menuOpen) return;
        const close = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) setMenuOpen(false);
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, [menuOpen]);

    const accountLinks: NavItem[] = [
        { to: "/my-profile", label: "My profile", icon: "contacts" },
        ...(user && ["staff", "supervisor", "manager", "admin"].includes(user.role)
            ? [{ to: "/my-pay", label: "My pay", icon: "billing" }] : []),
        ...(user?.role === "admin"
            ? [{ to: "/settings", label: "Settings", icon: "settings" }] : []),
    ];

    const groups = user?.role === "partner" ? PARTNER_GROUPS
        : user?.role === "support" ? SUPPORT_GROUPS
        : surface === "crm" ? CRM_GROUPS : SHIFTFLOW_GROUPS;

    /**
     * A user with no explicit grants sees whatever their role allows — that is
     * the state every account starts in. The moment an admin ticks any boxes
     * for them, the ticks become the whole truth for permissioned items.
     */
    const permitted = (item: NavItem): boolean => {
        if (!user) return false;
        if (item.roles && !item.roles.includes(user.role)) return false;
        if (item.permission && user.permissions.length > 0 && !user.permissions.includes(item.permission)) return false;
        if (item.quality && !item.quality.some(mayQuality)) return false;
        return true;
    };

    const visible = groups
        .map((group) => ({ ...group, items: group.items.filter(permitted) }))
        .filter((group) => group.items.length > 0);

    const heading = TITLES[location.pathname] ?? { title: "TrustCare", subtitle: "" };

    const switchTo = (next: Surface) => {
        setSurface(next);
        // Land on that surface's home rather than a page it doesn't have.
        window.history.pushState({}, "", next === "crm" ? "/crm" : "/");
        window.dispatchEvent(new PopStateEvent("popstate"));
    };

    return (
        <div className="shell">
            <aside className="sidebar">
                <div className="brand">
                    <img
                        src={branding.logoUrl ?? "/brand/icon.png"}
                        alt=""
                        width={34} height={34}
                        style={branding.logoUrl ? { objectFit: "contain", background: "#fff", borderRadius: 6, padding: 2 } : undefined}
                    />
                    <div className="brand-text">
                        <strong>{branding.orgName}</strong>
                        <span>{user?.role === "partner" ? "Partner Portal"
                            : user?.role === "support" ? "IT Support"
                            : surface === "crm" ? "Management System" : "ShiftFlow"}</span>
                    </div>
                </div>

                {user?.role !== "partner" && (mayAccess("shiftflow") || mayAccess("crm")) && (
                    <div className="surface-toggle" role="tablist" aria-label="Application area">
                        <button
                            role="tab"
                            aria-selected={surface === "shiftflow"}
                            className={surface === "shiftflow" ? "on" : ""}
                            onClick={() => mayAccess("shiftflow") && switchTo("shiftflow")}
                            disabled={!mayAccess("shiftflow")}
                            title={mayAccess("shiftflow") ? undefined : "You do not have ShiftFlow access"}
                        >
                            ShiftFlow
                        </button>
                        <button
                            role="tab"
                            aria-selected={surface === "crm"}
                            className={surface === "crm" ? "on" : ""}
                            onClick={() => mayAccess("crm") && switchTo("crm")}
                            disabled={!mayAccess("crm")}
                            title={mayAccess("crm") ? undefined : "You do not have CRM access"}
                        >
                            CRM
                        </button>
                    </div>
                )}

                <nav className="nav">
                    {visible.map((group, index) => (
                        <div key={group.heading ?? index}>
                            {group.heading && <div className="nav-group">{group.heading}</div>}
                            {group.items.map((item) => {
                                if (item.feature && !entitlements.enabled(item.feature)) {
                                    // Shown-and-locked: the feature advertises itself.
                                    return (
                                        <span key={item.to} className="nav-locked"
                                            title={`${FEATURE_LABELS[item.feature].name} — not enabled on your plan. An administrator can enable it under Settings, Plan & features.`}>
                                            <Icon name={item.icon} />
                                            <span>{item.label}</span>
                                            <span aria-hidden="true" style={{ marginLeft: "auto" }}>🔒</span>
                                        </span>
                                    );
                                }
                                return (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    end={["/", "/crm", "/partner", "/support"].includes(item.to)}
                                    className={({ isActive }) => (isActive ? "active" : "")}
                                >
                                    <Icon name={item.icon} />
                                    <span>{item.label}</span>
                                    {badgeFor(item.to) !== null && (
                                        <span className="nav-unread" aria-label={`${badgeFor(item.to)} unread messages`}>
                                            {badgeFor(item.to)}
                                        </span>
                                    )}
                                </NavLink>
                                );
                            })}
                        </div>
                    ))}
                </nav>

                <div className="who-wrap" ref={menuRef}>
                    <button
                        className="who"
                        aria-haspopup="menu"
                        aria-expanded={menuOpen}
                        onClick={() => setMenuOpen((open) => !open)}
                    >
                        <div className="avatar">{initials(user?.fullName || user?.email || "?")}</div>
                        <div style={{ minWidth: 0, flex: 1, textAlign: "left" }}>
                            <div className="who-name">{user?.fullName || user?.email}</div>
                            <div className="who-role">{user?.role}</div>
                        </div>
                        <svg className={`who-chevron${menuOpen ? " open" : ""}`} width="14" height="14"
                            viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                            strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="m6 9 6 6 6-6" />
                        </svg>
                    </button>
                    {menuOpen && (
                        <div className="who-menu" role="menu">
                            {accountLinks.map((item) => (
                                <NavLink key={item.to} to={item.to} role="menuitem"
                                    className={({ isActive }) => (isActive ? "active" : "")}>
                                    <Icon name={item.icon} />
                                    <span>{item.label}</span>
                                </NavLink>
                            ))}
                            <button className="who-menu-signout" role="menuitem" onClick={signOut}>
                                <svg className="nav-icon" width="16" height="16" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" strokeWidth="1.8"
                                    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                                    <path d="m16 17 5-5-5-5" /><path d="M21 12H9" />
                                </svg>
                                <span>Sign out</span>
                            </button>
                        </div>
                    )}
                </div>

                <div className="sign-out">
                    {/* Sign out lives in the account menu under the name.
                        Provenance below is rendered unconditionally — there is no
                        setting, no flag and no API field that removes it. A
                        white-labelled instance carries the buyer's identity above
                        and this below. */}
                    <div className="powered-by">
                        <img src="/brand/icon.png" alt="" width={14} height={14} />
                        <span>Powered by TrustCare</span>
                    </div>
                </div>
            </aside>

            <div className="main">
                {user?.accountKind === "auditor" && (
                    // 095: the window is named on every page. The middleware
                    // enforces read-only and the end date regardless.
                    <div style={{ background: "var(--warn-bg, #7a5b00)", color: "#fff",
                        padding: "0.35rem 1rem", fontSize: "0.82rem", textAlign: "center" }}>
                        Auditor access — read-only
                        {user.accessEndsAt
                            ? `, until ${new Date(user.accessEndsAt).toLocaleDateString("en-AU")}`
                            : ""}. Every view is recorded.
                    </div>
                )}
                <header className="topbar">
                    <div>
                        <h1>{heading.title}</h1>
                        {heading.subtitle && <p>{heading.subtitle}</p>}
                    </div>
                    <NotificationBell />
                </header>
                <div className="content watermarked">
                    <Outlet />
                </div>
            </div>
        </div>
    );
};
